import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaArrowUpRightFromSquare,
  FaFlask,
  FaClipboardCheck,
  FaSitemap,
  FaArrowTrendUp,
  FaGears,
  FaChartColumn,
  FaQuoteLeft,
  FaGraduationCap,
  FaPenNib,
  FaBookOpen,
  FaBullseye,
} from "react-icons/fa6";

const Home = () => {
  React.useEffect(() => {
    document.title = 'Learning Outcomes Academy — Tailored education, research, and accreditation';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="flex flex-col">
      <Hero />
      <PillarBar />
      <About />
      <Offerings />
      <Research />
      <Accreditation />
      <Programs />
      <FutureTools />
      <ClosingCTA />
    </div>
  );
};

/* ------------------------------ HERO ------------------------------ */
const Hero = () => (
  <section className="relative bg-bone overflow-hidden">
    <div className="container-page pt-14 lg:pt-20 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
      {/* LEFT */}
      <div className="lg:col-span-7">
        <h1 className="font-display text-[44px] sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter2 text-ink">
          Empowering{" "}
          <span className="italic font-normal text-brand-deep">learning,</span>{" "}
          <span className="italic font-normal text-brand-aubergine">research,</span>{" "}
          accreditation, and{" "}
          <span className="italic font-normal text-brand-moss">innovation</span>{" "}
          through tailored education.
        </h1>
        <p className="mt-7 max-w-xl text-lg text-slate2 leading-relaxed">
          We help educators, researchers, students, institutions, and lifelong learners
          strengthen academic and professional success through specialized courses,
          research support, accreditation preparation, program development, and
          future-ready digital tools.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link to="/courses" className="btn-primary">
            Explore Learning Solutions <FaArrowRight className="text-xs" />
          </Link>
          <Link to="/accreditation" className="btn-secondary">
            Prepare for Accreditation
          </Link>
        </div>
      </div>

      {/* RIGHT — layered editorial panel */}
      <div className="lg:col-span-5 relative">
        <div className="relative">
          {/* back panel */}
          <div className="absolute -top-6 -right-4 sm:-right-8 w-3/4 h-44 bg-brand-sage/40 rounded-2xl rotate-[-3deg]" />
          {/* main card */}
          <div className="relative bg-white rounded-2xl border border-ink/5 shadow-soft p-6 sm:p-8">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate2">
              Who we serve
            </div>
            <div className="mt-6 grid grid-cols-2 gap-y-7">
              {[
                { k: "Learners", v: "Students · Lifelong learners" },
                { k: "Educators", v: "Faculty · Instructors" },
                { k: "Researchers", v: "Early-career & seasoned" },
                { k: "Institutions", v: "Programs · Departments" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-display text-xl sm:text-3xl text-ink leading-none">
                    {s.k}
                  </div>
                  <div className="mt-2 text-sm text-slate2">{s.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-ink/10 flex items-start gap-3">
              <FaQuoteLeft className="text-brand-deep mt-1" />
              <p className="text-[15px] text-ink/80 leading-relaxed">
                <span className="font-display italic">
                  Bridging education, research, technology, and institutional
                  development
                </span>{" "}
                — built for learners and organizations that measure impact.
              </p>
            </div>
          </div>

          {/* small floating tag — links to About */}
          <Link
            to="/about"
            className="group absolute -bottom-5 -left-3 sm:-left-6 bg-ink text-bone rounded-xl px-4 py-3 shadow-soft hidden sm:flex items-center gap-3 hover:bg-charcoal transition-colors"
          >
            <FaArrowTrendUp className="text-brand-sage" />
            <div className="text-[11px] tracking-[0.16em] uppercase">
              From knowledge to measurable impact
            </div>
            <FaArrowRight className="text-[10px] text-brand-sage transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* --------------------- PILLAR BAR (small marquee) --------------------- */
const PillarBar = () => {
  const items = [
    "Tailored Courses",
    "Research Workshops",
    "Accreditation Preparation",
    "Program Development",
    "Thrive with AI",
    "Curriculum Alignment",
    "Self-Study Reports",
    "Future Digital Tools",
  ];
  return (
    <div className="bg-ink text-bone/90 border-y border-ink">
      <div className="container-page py-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-3 text-[11px] tracking-[0.22em] uppercase">
        {items.map((t) => (
          <span key={t} className="flex items-center gap-3">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sage" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------ ABOUT ------------------------------ */
const About = () => (
  <section className="bg-bone">
    <div className="container-page py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <div className="eyebrow eyebrow-bar">About</div>
        <h2 className="mt-5 font-display text-4xl lg:text-5xl leading-tight tracking-tightish text-ink">
          A forward-thinking education and innovation platform.
        </h2>
        <Link to="/about" className="group btn-link mt-8">
          More about us
          <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
        <div>
          <div className="h-px w-10 bg-brand-deep mb-4" />
          <h3 className="font-display text-xl text-ink">For people</h3>
          <p className="mt-2 text-slate2 leading-relaxed">
            Tailored learning for parents, students, educators, researchers, and
            professionals — including unique experiences such as AI for Parents.
          </p>
        </div>
        <div>
          <div className="h-px w-10 bg-brand-deep mb-4" />
          <h3 className="font-display text-xl text-ink">For institutions</h3>
          <p className="mt-2 text-slate2 leading-relaxed">
            Accreditation preparation, program development support, and curriculum
            alignment that strengthens learning at the program level.
          </p>
        </div>
        <div>
          <div className="h-px w-10 bg-brand-deep mb-4" />
          <h3 className="font-display text-xl text-ink">For research</h3>
          <p className="mt-2 text-slate2 leading-relaxed">
            Workshops and guidance across the research journey — from study design and
            data analysis to writing for publication and peer review.
          </p>
        </div>
        <div>
          <div className="h-px w-10 bg-brand-deep mb-4" />
          <h3 className="font-display text-xl text-ink">For the future</h3>
          <p className="mt-2 text-slate2 leading-relaxed">
            Cloud-based tools in development for data analysis and academic
            workflows, beginning with the Research Data Analyzer — with more tools
            on the way.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ----------------------------- CORE OFFERINGS ----------------------------- */
const Offerings = () => (
  <section id="offerings" className="bg-cream">
    <div className="container-page py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-7">
          <div className="eyebrow eyebrow-bar">Core Offerings</div>
          <h2 className="mt-5 font-display text-4xl lg:text-5xl leading-tight tracking-tightish text-ink">
            Several practices, designed for the way modern learning actually
            works.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-slate2 leading-relaxed">
            Each practice stands alone and works together. Choose a course, bring a
            workshop to your team, prepare for accreditation, or partner with us on a
            program review.
          </p>
        </div>
      </div>

      {/* Editorial layout: large feature row + supporting rows */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <FeatureLarge
          icon={<FaGraduationCap />}
          accent="bg-brand-deep text-bone"
          title="Tailored Courses"
          desc="Specialized, practical courses for modern learners, educators, families, and professionals — including AI for Parents, research skills, professional development, and academic and career advancement."
          bullets={[
            "AI for Parents",
            "Research skills",
            "Educator professional development",
            "Academic & career advancement",
          ]}
          href="/courses"
        />
        <FeatureMedium
          icon={<FaFlask />}
          title="Research Workshops"
          desc="Targeted workshops across the research journey — design, data, writing, peer review, and workflow improvement — for students, early-career researchers, faculty, and seasoned professionals."
          href="/research"
        />
        <FeatureMedium
          icon={<FaClipboardCheck />}
          title="Accreditation Preparation"
          desc="A confident, structured path through accreditation: process overview, self-study report writing, visit preparation, evidence organization, and readiness planning."
          href="/accreditation"
          span="lg:col-span-6"
        />
        <FeatureMedium
          icon={<FaGears />}
          title="Future Digital Tools"
          desc="A focused suite of cloud-based tools in development for researchers, educators, and accreditation teams — starting with the Research Data Analyzer, with more on the way."
          href="/tools"
          span="lg:col-span-6"
        />
        <FeatureWide
          icon={<FaSitemap />}
          title="Program Development Support"
          desc="Help educators and institutions design, improve, assess, and evaluate academic and professional programs — from learning outcomes and curriculum alignment to assessment planning, program review, and continuous improvement."
          href="/programs"
        />
      </div>
    </div>
  </section>
);

const FeatureLarge = ({ icon, accent, title, desc, bullets, href }) => (
  <article
    className={`lg:col-span-7 ${accent} rounded-3xl p-8 sm:p-10 relative overflow-hidden flex flex-col`}
  >
    <div className="flex items-start justify-between text-bone/70">
      <div className="text-3xl">{icon}</div>
    </div>
    <h3 className="mt-8 font-display text-3xl sm:text-4xl leading-tight tracking-tightish">
      {title}
    </h3>
    <p className="mt-5 text-bone/85 leading-relaxed max-w-2xl">{desc}</p>
    <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-bone/85">
      {bullets.map((b) => (
        <li key={b} className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-sage" /> {b}
        </li>
      ))}
    </ul>
    <Link
      to={href}
      className="mt-8 inline-flex items-center gap-2 text-bone border-b border-bone/40 hover:border-bone w-fit pb-1"
    >
      Browse courses <FaArrowRight className="text-xs" />
    </Link>
  </article>
);

const FeatureMedium = ({ icon, title, desc, href, span = "lg:col-span-5" }) => (
  <article className={`${span} bg-white rounded-3xl p-8 border border-ink/5 shadow-soft flex flex-col`}>
    <div className="flex items-start justify-between text-ink/70">
      <div className="text-2xl text-brand-deep">{icon}</div>
    </div>
    <h3 className="mt-6 font-display text-2xl text-ink leading-tight">{title}</h3>
    <p className="mt-3 text-slate2 leading-relaxed">{desc}</p>
    <Link to={href} aria-label={`Learn more about ${title}`} className="group btn-link mt-6 w-fit">
      Learn more
      <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
    </Link>
  </article>
);

const FeatureWide = ({ icon, title, desc, href }) => (
  <article className="lg:col-span-12 bg-white rounded-3xl p-8 sm:p-10 border border-ink/5 shadow-soft grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
    <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-4">
      <div className="text-3xl text-brand-aubergine">{icon}</div>
    </div>
    <div className="lg:col-span-7">
      <h3 className="font-display text-2xl sm:text-3xl text-ink leading-tight">
        {title}
      </h3>
      <p className="mt-3 text-slate2 leading-relaxed">{desc}</p>
    </div>
    <div className="lg:col-span-3 lg:text-right">
      <Link to={href} className="btn-secondary">
        Explore programs <FaArrowRight className="text-xs" />
      </Link>
    </div>
  </article>
);

/* ----------------------------- RESEARCH ----------------------------- */
const Research = () => {
  const stages = [
    { icon: <FaPenNib />, title: "Research design", text: "Frame questions, scope studies, and select methods that fit your context." },
    { icon: <FaChartColumn />, title: "Data analysis", text: "Apply quantitative and qualitative analysis with reproducible workflows." },
    { icon: <FaBookOpen />, title: "Writing for publication", text: "Build manuscripts, refine arguments, and target the right journals." },
  ];

  return (
    <section className="bg-bone">
      <div className="container-page py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-5">
            <div className="eyebrow eyebrow-bar">Research Support</div>
            <h2 className="mt-5 font-display text-4xl lg:text-5xl leading-tight tracking-tightish text-ink">
              Practical support across the entire research journey.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-ink/10">
            <p className="text-lg text-slate2 leading-relaxed">
              From planning a first study to publishing established work, our research
              workshops and one-on-one guidance meet researchers where they are.
              Sessions are practical, applied, and built around real projects — not
              abstract theory.
            </p>
          </div>
        </div>

        {/* preview row — three of six */}
        <div className="rounded-3xl border border-ink/10 bg-white overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ink/10">
            {stages.map((s) => (
              <div key={s.title} className="p-7">
                <span className="text-brand-deep text-xl">{s.icon}</span>
                <h3 className="mt-4 font-display text-xl text-ink">{s.title}</h3>
                <p className="mt-2 text-slate2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-6 flex-wrap">
          <p className="text-sm text-ink/60">
            Plus data collection, peer review preparation, and workflow improvement.
          </p>
          <Link to="/research" className="group btn-link">
            See all research workshops
            <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ----------------------------- ACCREDITATION ----------------------------- */
const Accreditation = () => (
  <section className="bg-ink text-bone relative overflow-hidden">
    <div className="container-page py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="eyebrow !text-brand-sage">
          <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
          Accreditation
        </div>
        <h2 className="mt-5 font-display text-4xl lg:text-5xl leading-tight tracking-tightish">
          Confident, structured preparation built for institutional realities.
        </h2>
        <p className="mt-6 text-bone/75 leading-relaxed">
          Whether you are approaching a first accreditation cycle or improving an
          established process, our team helps you organize evidence, write a clearer
          self-study, and arrive at the visit ready.
        </p>
        <Link
          to="/accreditation"
          className="mt-8 inline-flex items-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-cream transition-colors"
        >
          Plan your readiness <FaArrowRight className="text-xs" />
        </Link>
      </div>

      <div className="lg:col-span-7">
        <ol className="space-y-3">
          {[
            { t: "Accreditation process overview", d: "Map the standards, the cycle, and the players that matter for your program." },
            { t: "Writing effective self-study reports", d: "Structure narratives that clearly demonstrate compliance and continuous improvement." },
            { t: "Preparing for accreditation visits", d: "Brief your team, rehearse interviews, and organize documents for site reviewers." },
          ].map((row) => (
            <li
              key={row.t}
              className="group flex items-start gap-4 rounded-xl px-4 py-4 border border-bone/10 hover:border-bone/30 hover:bg-bone/5 transition"
            >
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sage" />
              <div className="flex-1">
                <h3 className="font-display text-xl">{row.t}</h3>
                <p className="mt-1 text-bone/70 leading-relaxed">{row.d}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-sm text-bone/55">
          Plus readiness planning and evidence organization on the full page.
        </p>
      </div>
    </div>
  </section>
);

/* ----------------------------- PROGRAM DEVELOPMENT ----------------------------- */
const Programs = () => {
  const highlights = [
    { icon: <FaBullseye />, title: "Learning outcomes development", text: "Define program-level outcomes that are measurable, meaningful, and aligned to standards." },
    { icon: <FaSitemap />, title: "Curriculum alignment", text: "Map courses, units, and assessments so the curriculum tells one coherent story." },
    { icon: <FaClipboardCheck />, title: "Assessment planning", text: "Design assessments that capture what students actually learn — without burying faculty." },
  ];
  return (
    <section className="bg-cream">
      <div className="container-page py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-7">
            <div className="eyebrow eyebrow-bar">Program Development</div>
            <h2 className="mt-5 font-display text-4xl lg:text-5xl leading-tight tracking-tightish text-ink">
              Designed, aligned, assessed: programs that actually deliver outcomes.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate2 leading-relaxed">
              We partner with faculty, departments, and education leaders to design
              new programs and strengthen existing ones — with a clear thread from
              learning outcomes to curriculum to assessment.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {highlights.map((h) => (
            <div
              key={h.title}
              className="bg-white rounded-2xl border border-ink/10 p-7"
            >
              <div className="text-brand-aubergine text-xl">{h.icon}</div>
              <h3 className="mt-4 font-display text-xl text-ink">{h.title}</h3>
              <p className="mt-2 text-slate2 leading-relaxed">{h.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between gap-6 flex-wrap">
          <p className="text-sm text-ink/60">
            Plus program review, continuous improvement, and outcome-based education support.
          </p>
          <Link to="/programs" className="group btn-link">
            Program development services
            <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ----------------------------- FUTURE TOOLS ----------------------------- */
const FutureTools = () => (
  <section className="bg-bone">
    <div className="container-page py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
        <div className="lg:col-span-5">
          <div className="eyebrow eyebrow-bar">Tools · In Development</div>
          <h2 className="mt-5 font-display text-4xl lg:text-5xl leading-tight tracking-tightish text-ink">
            State-of-the-art solutions{" "}
            <span className="italic text-brand-deep">on the way.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pl-12">
          <p className="text-lg text-slate2 leading-relaxed">
            We are building a small, focused suite of cloud tools that extend our
            services into the everyday work of researchers and accreditation teams.
            These tools are planned and in development — not yet launched. Sign up to
            be notified when each becomes available.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ToolCard
          icon={<FaChartColumn />}
          status="In development"
          name="Research Data Analyzer"
          tagline="AI-driven analytics for researchers."
          desc="Process, analyze, and visualize complex datasets with greater ease, clarity, and efficiency — designed for real research workflows, not generic dashboards."
          features={[
            "Quant + qual analysis",
            "Reproducible pipelines",
            "Publication-ready visuals",
          ]}
        />
        <article className="rounded-3xl border border-dashed border-ink/20 bg-cream/60 p-8 sm:p-10 flex flex-col justify-between relative overflow-hidden">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-brand-deep">
              More tools
            </div>
            <h3 className="mt-3 font-display text-3xl text-ink leading-tight">
              Additional tools{" "}
              <span className="italic text-ink/50">in development.</span>
            </h3>
            <p className="mt-4 text-slate2 leading-relaxed">
              We are developing new tools to support researchers, educators, and
              institutions. We will share more as each becomes available.
            </p>
          </div>
          <Link to="/contact" className="group btn-link mt-7 w-fit">
            Get notified{" "}
            <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
          </Link>
        </article>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-ink/10 bg-white px-6 py-5">
        <div className="flex items-center gap-4">
          <span className="font-display italic text-ink/40">future</span>
          <p className="text-ink/80">
            Have a tool you wish existed for research, accreditation, or program
            assessment?
          </p>
        </div>
        <Link to="/contact" className="group btn-link">
          Tell us about it{" "}
          <FaArrowUpRightFromSquare className="text-xs transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      <div className="mt-8 flex justify-end">
        <Link to="/tools" className="group btn-link">
          Explore all tools
          <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  </section>
);

const ToolCard = ({ icon, status, name, tagline, desc, features }) => (
  <article className="bg-white rounded-3xl border border-ink/10 p-8 sm:p-10 flex flex-col relative overflow-hidden">
    <div className="absolute top-6 right-6 inline-flex items-center gap-2 rounded-full bg-brand-sage/30 text-brand-deep text-[11px] tracking-[0.16em] uppercase px-3 py-1">
      <span className="h-1.5 w-1.5 rounded-full bg-brand-deep" />
      {status}
    </div>
    <div className="text-3xl text-brand-deep">{icon}</div>
    <h3 className="mt-6 font-display text-3xl text-ink leading-tight">{name}</h3>
    <p className="mt-2 font-display italic text-slate2">{tagline}</p>
    <p className="mt-4 text-slate2 leading-relaxed">{desc}</p>

    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
      {features.map((f) => (
        <li
          key={f}
          className="rounded-lg border border-ink/10 px-3 py-2 text-ink/80 bg-bone"
        >
          {f}
        </li>
      ))}
    </ul>

    <Link to="/tools" className="group btn-link mt-7 w-fit">
      View tool details
      <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
    </Link>
  </article>
);

/* ----------------------------- CLOSING CTA ----------------------------- */
const ClosingCTA = () => (
  <section className="bg-bone">
    <div className="container-page pb-20 lg:pb-28">
      <div className="rounded-3xl bg-ink text-bone p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-brand-aubergine/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-brand-moss/20 rounded-full blur-3xl" />

        <div className="lg:col-span-7 relative">
          <div className="eyebrow !text-brand-sage">
            <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
            Begin with us
          </div>
          <h2 className="mt-5 font-display text-4xl lg:text-5xl leading-tight tracking-tightish">
            Move from knowledge to{" "}
            <span className="italic text-brand-sage">measurable impact.</span>
          </h2>
          <p className="mt-5 text-bone/75 max-w-xl leading-relaxed">
            Explore a course, bring a workshop to your team, prepare for accreditation,
            or partner with us on a program review. We will meet you where you are and
            help you build from there.
          </p>
        </div>

        <div className="lg:col-span-5 relative flex flex-col gap-3">
          <Link
            to="/courses"
            className="inline-flex items-center justify-between gap-4 bg-bone text-ink font-medium px-6 py-4 rounded-full hover:bg-cream transition-colors"
          >
            Explore learning solutions <FaArrowRight />
          </Link>
          <Link
            to="/research"
            className="inline-flex items-center justify-between gap-4 border border-bone/30 text-bone font-medium px-6 py-4 rounded-full hover:bg-bone/10 transition-colors"
          >
            Discover research support <FaArrowRight />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-between gap-4 border border-bone/30 text-bone font-medium px-6 py-4 rounded-full hover:bg-bone/10 transition-colors"
          >
            Talk to our team <FaArrowRight />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default Home;
